
import { defineComponent, reactive } from "vue";
import store from "@/store";
import router from "@/router";
import CourseGroupsService from "@/api/Aula/CourseGroupsService";
import CoursesService from "@/api/Aula/CoursesSevice";
import HeadquartersService from "@/api/Configuration/HeadquartersService";
import AcademicYearService from "@/api/Configuration/AcademicYearService";
import TeachersService from "@/api/Aula/TeachersService";
import PreschoolTeacherAssignmentService from "@/api/Preschool/PreschoolTeacherAssignmentService";

import type {
  ICourseGroupsModel,
  IHeadquartersModel,
  IAcademicYearModel,
  ICoursesModel,
  ITeachersModel,
  IPreschoolTeacherAssignmentsModel,
} from "@/interfaces/modules";
import { SHOW_PRESCHOOL } from "@/enums";

export default defineComponent({
  name: "PreschoolTeacherAssignments",
  data() {
    return {
      // Asegurar que sea numérico
      assignmentId: Number(this.$route.params.Id || 0),
      table: {
        loading: false,
      } as any,

      // Catálogos
      groupsRaw: [] as ICourseGroupsModel[],
      courses: [] as ICoursesModel[],
      groups: [] as Array<
        ICourseGroupsModel & {
          CourseName?: string;
          HeadquarterId?: number;
          AcademicYearId?: number;
          HeadquarterName?: string;
          AcademicYearName?: string;
        }
      >,
      headquarters: [] as IHeadquartersModel[],
      academicYears: [] as IAcademicYearModel[],
      teachers: [] as ITeachersModel[],

      // Asignaciones actuales (fuente de verdad)
      assignments: [] as IPreschoolTeacherAssignmentsModel[],

      filters: reactive({
        headquarterId: null as number | null,
        academicYearId: null as number | null,
        courseGroupId: null as number | null,
        teacherId: null as number | null,
      }),
    };
  },

  computed: {
    headquarterOptions(): Array<{ value: number; text: string }> {
      return (this.headquarters || [])
        .map((h) => ({ value: h.Id, text: h.Description }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    academicYearOptions(): Array<{ value: number; text: string }> {
      return (this.academicYears || [])
        .map((y) => ({ value: y.Id, text: `Año ${y.Year}` }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    groupOptions(): Array<{ value: number; text: string }> {
      return (this.groups || [])
        .map((g) => ({ value: g.Id, text: g.Name || `Grupo ${g.Id}` }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    teacherOptions(): Array<{ value: number; text: string }> {
      return (this.teachers || [])
        .map((t) => ({ value: t.Id, text: this.teacherLabel(t) }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    groupsForTree(): Array<
      ICourseGroupsModel & {
        CourseName?: string;
        HeadquarterName?: string;
        AcademicYearName?: string;
      }
    > {
      const byHQ = (g: any) =>
        this.filters.headquarterId == null ||
        Number(g.HeadquarterId) === Number(this.filters.headquarterId);
      const byYear = (g: any) =>
        this.filters.academicYearId == null ||
        Number(g.AcademicYearId) === Number(this.filters.academicYearId);
      const byGroup = (g: any) =>
        this.filters.courseGroupId == null ||
        Number(g.Id) === Number(this.filters.courseGroupId);

      return (this.groups || [])
        .filter((g) => byHQ(g) && byYear(g) && byGroup(g))
        .sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
    },

    // IMPORTANTE: aquí ya no usamos assignmentId como teacher,
    // confiamos en teachers filtrados + filters.teacherId
    teachersForUI(): ITeachersModel[] {
      return (this.teachers || []).filter(
        (t) =>
          this.filters.teacherId == null ||
          Number(t.Id) === Number(this.filters.teacherId)
      );
    },

    canRegister(): boolean {
      // Botón habilitado solo si hay al menos una asignación activa
      return this.assignments.some(a => a.Status);
    },

    isEditing(): boolean {
      return this.assignmentId > 0;
    },
  },

  async mounted() {
    await this.load();
  },

  methods: {
    teacherLabel(t: any) {
      return `${t.Name} ${t.LastName || ""}`.trim();
    },

    async load() {
      try {
        this.table.loading = true;

        const [
          groups,
          courses,
          headquarters,
          years,
          teachers,
          assignments,
        ] = await Promise.all([
          CourseGroupsService.GetCourseGroups(),                // todos los grupos
          CoursesService.GetCourses(SHOW_PRESCHOOL.Yes),        // solo cursos preescolar
          HeadquartersService.GetHeadquarters(),
          AcademicYearService.GetAcademicYears(),
          TeachersService.GetTeachers(),
          PreschoolTeacherAssignmentService.GetAll?.() ?? Promise.resolve([]),
        ]);

        this.groupsRaw = groups || [];
        this.courses = courses || [];
        this.headquarters = headquarters || [];
        this.academicYears = years || [];
        this.teachers = teachers || [];
        this.assignments = assignments || [];

        const crsById = new Map(this.courses.map((c) => [Number(c.Id), c]));
        const hqById = new Map(this.headquarters.map((h) => [Number(h.Id), h]));
        const yrById = new Map(this.academicYears.map((y) => [Number(y.Id), y]));

        // Enriquecer grupos con info de curso/sede/año
        this.groups = this.groupsRaw
          .filter((g: ICourseGroupsModel) => crsById.has(Number(g.CourseId)))
          .map((g: ICourseGroupsModel) => {
            const course = crsById.get(Number(g.CourseId));
            const headquarterId = course?.HeadquarterId ?? undefined;
            const yearId = course?.AcademicYearId ?? g.AcademicYearId;
            return {
              ...g,
              CourseName: course?.Name ?? "",
              HeadquarterId: headquarterId,
              AcademicYearId: yearId,
              HeadquarterName: headquarterId
                ? hqById.get(headquarterId)?.Description ?? ""
                : "",
              AcademicYearName: yearId
                ? `Año ${yrById.get(yearId)?.Year ?? ""}`
                : "",
            };
          });

        // ===== MODO EDICIÓN: assignmentId es el Id de IPreschoolTeacherAssignmentsModel =====
        if (this.assignmentId > 0) {
          const baseAssignment = this.assignments.find(
            a => Number(a.Id) === this.assignmentId
          );

          if (baseAssignment) {
            const teacherId = baseAssignment.TeacherId;
            const yearId = baseAssignment.AcademicYearId;

            // Quedarnos SOLO con las asignaciones de ese docente y ese año
            const teacherYearAssignments = this.assignments.filter(
              a =>
                Number(a.TeacherId) === Number(teacherId) &&
                Number(a.AcademicYearId) === Number(yearId)
            );
            this.assignments = teacherYearAssignments;

            // Fijar filtros para ese docente y año
            this.filters.teacherId = teacherId;
            this.filters.academicYearId = yearId;

            // Limitar docentes a ese docente
            this.teachers = this.teachers.filter(
              t => Number(t.Id) === Number(teacherId)
            );

            console.log("Asignaciones del docente para edición:", this.teachers);

            // Limitar grupos solo a los grupos donde ese docente tiene asignación
            const groupIdsForTeacher = new Set(
              teacherYearAssignments.map(a => a.GroupId)
            );
            this.groups = this.groups.filter(g => groupIdsForTeacher.has(g.Id));
          }
        }
      } finally {
        this.table.loading = false;
      }
    },

    // Helpers de asignaciones
    academicYearForGroup(grp: any): number {
      // Usa filtro si está definido, sino el año del grupo
      return Number(this.filters.academicYearId || grp.AcademicYearId || 0);
    },

    assignmentIndex(grp: any, tch: ITeachersModel): number {
      const yearId = this.academicYearForGroup(grp);
      return this.assignments.findIndex(
        (a) =>
          Number(a.GroupId) === Number(grp.Id) &&
          Number(a.TeacherId) === Number(tch.Id) &&
          Number(a.AcademicYearId) === Number(yearId)
      );
    },

    isAssigned(grp: any, tch: ITeachersModel): boolean {
      const idx = this.assignmentIndex(grp, tch);
      if (idx === -1) return false;
      return !!this.assignments[idx].Status;
    },

    onToggleAssignment(grp: any, tch: ITeachersModel, newVal: boolean) {
      const yearId = this.academicYearForGroup(grp);
      if (!yearId) {
        store.dispatch("core/popupDialog", {
          text: "Debe seleccionar un año académico para registrar la asignación.",
          type: "warning",
          showCancelButton: false,
        });
        return;
      }

      const idx = this.assignmentIndex(grp, tch);

      if (newVal) {
        // Marcar como asignado (crear o actualizar)
        if (idx === -1) {
          this.assignments.push({
            Id: 0, // nuevo
            TeacherId: tch.Id,
            GroupId: grp.Id,
            AcademicYearId: yearId,
            Status: true,
          } as IPreschoolTeacherAssignmentsModel);
        } else {
          this.assignments[idx].Status = true;
        }
      } else {
        // Marcar como no asignado (Status = false)
        if (idx !== -1) {
          this.assignments[idx].Status = false;
        }
      }
    },

    teacherCountForGroup(groupId: number): number {
      return this.assignments.filter(
        (a) => Number(a.GroupId) === Number(groupId) && a.Status
      ).length;
    },

    buildPayload(): IPreschoolTeacherAssignmentsModel[] {
      return this.assignments;
    },

    async onRegisterAll() {
      const payload = this.buildPayload();

      if (!payload || payload.length === 0) {
        store.dispatch("core/popupDialog", {
          text: "No hay asignaciones para registrar.",
          type: "warning",
          showCancelButton: false,
        });
        return;
      }

      try {
        this.table.loading = true;

        for (const item of payload) {
          await PreschoolTeacherAssignmentService.Post(item as any);
        }

        store.dispatch("core/popupDialog", {
          text: "Asignaciones de preescolar guardadas con éxito.",
          type: "success",
          showCancelButton: false,
        });

        router.back();
      } catch (e) {
        console.error("Error registrando asignaciones de preescolar:", e);
        store.dispatch("core/popupDialog", {
          text: "Se produjo un error al guardar las asignaciones.",
          type: "error",
          showCancelButton: false,
        });
      } finally {
        this.table.loading = false;
      }
    },
  },
});
